import React, { FC, ReactElement, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { cx } from '@emotion/css';
import { Swiper, SwiperClass, SwiperProps, SwiperSlide } from 'swiper/react';
import { A11y, Navigation, Pagination, EffectCoverflow } from 'swiper/modules';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';
import { useBreakpoints } from '@phoenix/design-system/frontend/ui/breakpoints/use-breakpoints';

import style from './carrousel.module.scss';

export interface CarrouselProps extends SwiperProps {
  slides?: ReactElement[];
  darkMode?: boolean;
}

export const Carrousel: FC<CarrouselProps> = ({ id, slides = [], className = '', darkMode, pagination, ...props }) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const { t } = useTranslation('shared');
  const { desktop } = useBreakpoints();

  const focusActiveSlide = () => {
    const activeSlide = swiper?.slides.find((el) => el.classList.contains('swiper-slide-active'));
    (activeSlide?.getElementsByTagName('article')[0] ?? activeSlide?.getElementsByTagName('a')[0])?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (swiper && !swiper.animating) {
      ({
        ArrowLeft: () => {
          swiper.slidePrev();
          focusActiveSlide();
        },
        ArrowRight: () => {
          swiper.slideNext();
          focusActiveSlide();
        },
      })[event.key]?.();
    }
  };

  return (
    <div className="relative" onKeyDown={onKeyDown}>
      <Swiper
        id={id}
        observer={true}
        observeParents={true}
        onSwiper={setSwiper}
        className={cx(style.carrousel, className)}
        modules={[EffectCoverflow, Pagination, Navigation, A11y]}
        effect="coverflow"
        coverflowEffect={{
          rotate: 0,
          stretch: -10,
          depth: 200,
          modifier: 1,
          slideShadows: false,
        }}
        pagination={
          typeof pagination === 'boolean'
            ? pagination
            : {
                el: '.pagination',
                dynamicBullets: true,
                ...pagination,
              }
        }
        navigation={{
          nextEl: `.nav-next_${id}`,
          prevEl: `.nav-prev_${id}`,
        }}
        wrapperClass={style.carrousel_wrapper}
        initialSlide={1}
        slidesPerView={desktop ? 3 : 1.1}
        centeredSlides={true}
        loop={slides.length > 3}
        data-testid={`carrousel-${id}`}
        a11y={{ enabled: true, prevSlideMessage: t('shared_slider_previousSlide'), nextSlideMessage: t('shared_slider_nextSlide') }}
        {...props}
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.key}>
            {({ isPrev, isActive, isNext }) => (
              <div
                className={cx(
                  style.carrousel_slide,
                  { 'shadow-lg': isActive },
                  (isPrev || isActive || isNext) && style.carrousel_slide__visible
                )}
              >
                {React.cloneElement(slide, { isPrev, isActive, isNext, isVisible: isPrev || isActive || isNext })}
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
      {slides.length > 3 && (
        <>
          <div className={cx(style.pagination, 'pagination')} />
          <div
            className={cx(
              style.swiper_button_prev,
              `swiper-button-prev nav-prev_${id}`,
              darkMode ? 'bg-secondary-light focus-visible:outline-primary-white' : 'focus-visible:outline-primary-black bg-secondary-deep'
            )}
            data-testid="arrow-left"
          >
            <div className={cx('h-3 w-3', { 'text-primary-black': darkMode })}>
              <PhoenixIcon icon={getDesignSystemIcon('icon-arrow-left-16.svg')} />
            </div>
          </div>
          <div
            className={cx(
              style.swiper_button_next,
              `swiper-button-next nav-next_${id} `,
              darkMode ? 'bg-secondary-light focus-visible:outline-primary-white' : 'focus-visible:outline-primary-black bg-secondary-deep'
            )}
            data-testid="arrow-right"
          >
            <div className={cx('h-3 w-3', { 'text-primary-black': darkMode })}>
              <PhoenixIcon icon={getDesignSystemIcon('icon-arrow-right-16.svg')} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
